import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { H2, H4, Paragraph, Text } from 'components/core/Typography';
import { Review } from 'components/reviewV2/Review';
import { ReviewList } from 'components/reviewV2/ReviewList';
import { ReviewSummary } from 'components/reviewV2/ReviewSummary';
import ThumbsUp from '../../svg/ThumbsUp';
import './facilityReviewsV2.scss';
import GoogleReviewCard from '../../reviewV2/GoogleReview/GoogleReviewCard';
import { Button } from 'components/core/Button';
import userPc from 'assets/images/icons/user-pc.svg';

const NoReviewBlock = ({ facilityId }) => {
	return <div className="no-review-block">
		<img
			src={userPc}
			alt="User PC"
			className="no-review-image"
		/>
		<H4 size="medium" family="rounded" color="black" weight="medium">No reviews Found</H4>
		<Paragraph weight="medium" color="black">Be the first to leave a review!</Paragraph>
		<Button
			href={`/review.html?fid=${facilityId}`}
			rel="nofollow"
		>
			<div><ThumbsUp/></div>
			<Paragraph size="small" weight="ultraBold">Leave a review</Paragraph>
		</Button>
	</div>
}

export function FacilityReviewsV2({
	facilityId,
	ratingCounts,
	pros,
	cons,
	reviews,
	reviewAverage,
	reviewCount,
	className,
	recommendedRate,
	facilityUrl,
	fullFacilityUrl,
	currentReviewPage,
	images,
	facilityName,
	locationText,
	googleReviews = {},
	showGoogleReviews = false,
}) {
	const reviewCountsNumber = Number(reviewCount) || 0;
	const [showVerified, setShowVerified] = useState(() => {
        if (reviewCountsNumber > 0) return true;
        if (showGoogleReviews && (googleReviews?.reviews?.length || 0) > 0) return false;
        return true;
    });

	const handleToggle = (value) => () =>{
		if (value === showVerified) return;
		setShowVerified(value);
	}

	function formatCountCompact(count) {
		count = Number(count) || 0;
		if (count < 1000) return String(count);
		if (count < 1_000_000) return `${Math.floor(count / 1000)}K+`;
		if (count < 1_000_000_000) return `${Math.floor(count / 1_000_000)}M+`;
		return `${Math.floor(count / 1_000_000_000)}B+`;
	}

	return (
		<div id="reviews" className={classnames(className, 'facility-reviews')}>
			<div className="reviews-row">
				<div className="left-col">
					<div className="reviews-header">
						<div className="reviews-title">
							<H2 size="large" color="black" weight="ultraBold">
								Customer Reviews
							</H2>
						</div>
						{
							googleReviews.reviews?.length > 0 && showGoogleReviews && (
								<div className='toggle-reviews'>
									<div
										className={`${showVerified ? 'active' : ''}`}
										onClick={handleToggle(true)}
										data-testid="verified-reviews-tab"
									>
										Verified {`(${reviewCount})`}
									</div>
									<div
										className={`${!showVerified ? 'active' : ''}`}
										onClick={handleToggle(false)}
										data-testid="google-reviews-tab"
									>
										Google {`(${formatCountCompact(googleReviews.userRatingsTotal)})`}
									</div>
								</div>
							)
						}

					</div>
					{
						showVerified ? (
							reviewCountsNumber > 0 && <ReviewList
								facilityUrl={facilityUrl}
								fullFacilityUrl={fullFacilityUrl}
								currentPage={currentReviewPage}
								ratingCounts={ratingCounts}
								totalReviews={reviewCount}
								pros={pros}
								cons={cons}
								recommendedRate={recommendedRate}
								facilityId={facilityId}
								reviewAverage={reviewAverage}
								verifiedReviewsCount={reviewCount}
							>
								{reviews.map((review) => (
									<Review
										key={review.id}
										id={review.id}
										reviewerName={review.nickname}
										reviewTitle={review.title}
										reviewText={review.message}
										reviewDate={new Date(review.date.format.iso)}
										reviewRating={review.rating}
										reviewRatingOutOf={5}
										priceRating={review.ratingDetails.ratingPrice}
										serviceRating={review.ratingDetails.ratingService}
										cleanlinessRating={review.ratingDetails.ratingCleanliness}
										recommended={review.ratingRecommended}
										managerResponse={review.managerResponse}
										images={images}
										facilityName={facilityName}
										address={locationText}
									/>
								))}

							</ReviewList>
						) : (
							<>
								{
									googleReviews.reviews && googleReviews.reviews?.length > 0 && <>
										<ReviewList
											facilityUrl={facilityUrl}
											fullFacilityUrl={fullFacilityUrl}
											currentPage={1}
											ratingCounts={ratingCounts}
											totalReviews={reviewCount}
											pros={pros}
											cons={cons}
											recommendedRate={recommendedRate}
											facilityId={facilityId}
											reviewAverage={reviewAverage}
											showPagination={false}
											verifiedReviewsCount={reviewCount}
											googleReviewsCount={googleReviews.user_ratings_total || 0}
											curentGoogleReviewCount={googleReviews.reviews?.length || 0}
											showVerified={false}
										>
											{googleReviews.reviews?.map((review) =>
												<GoogleReviewCard
													reviewerName={review.authorName}
													reviewerPhoto={review.profilePhotoUrl}
													rating={review.rating}
													reviewDate={new Date(review.time * 1000)}
													reviewText={review.text}
													key={review.authorName + review.time}
												/>
											)}
										</ReviewList>
										<Button
											href={`https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${googleReviews.placeId}`}
											rel="nofollow"
											className="google-reviews-button"
											target="_blank"
											data-testid="google-reviews-button"
										>
											<Text size="small" weight="ultraBold">Read all reviews on Google</Text>
										</Button>
									</>
								}
							</>
						)
					}
				</div>
				{
					reviewCountsNumber > 0 && <div className="right-col">
						<ReviewSummary
							ratingCounts={ratingCounts}
							totalReviews={reviewCount}
							pros={pros}
							cons={cons}
							recommendedRate={recommendedRate}
							facilityId={facilityId}
							reviewAverage={reviewAverage}
						/>
					</div>
				}
			</div>
			<div>
				{
					reviewCountsNumber === 0 && showVerified && <NoReviewBlock facilityId={facilityId} />
				}
			</div>
		</div>
	);
}

FacilityReviewsV2.propTypes = {
	reviews: PropTypes.arrayOf(PropTypes.object),
	reviewAverage: PropTypes.number,
	reviewCount: PropTypes.number.isRequired,
	ratingCounts: PropTypes.arrayOf(PropTypes.shape({
		rating: PropTypes.number.isRequired,
		count: PropTypes.number.isRequired
	})).isRequired,
	pros: PropTypes.arrayOf(PropTypes.object),
	cons: PropTypes.arrayOf(PropTypes.object),
	facilityId: PropTypes.number.isRequired,
	facilityUrl: PropTypes.string.isRequired,
	fullFacilityUrl: PropTypes.string.isRequired,
	recommendedRate: PropTypes.number,
	currentReviewPage: PropTypes.number,
	className: PropTypes.string,
	images: PropTypes.arrayOf(PropTypes.string),
	facilityName: PropTypes.string,
	googleReviews: PropTypes.object,
	showGoogleReviews: PropTypes.bool,
};

export default FacilityReviewsV2;


